import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { CalendarRange, CircleDollarSign, Download, MessageCircle, Search, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import { RootState } from '../../store/store'

type Lic = {
  user_id: string
  usuario_nombre: string
  usuario_email: string
  usuario_telefono?: string
  plan: string
  plan_type?: string
  estado: string
  days_left: number | null
  valid_until?: string | null
  start_date?: string | null
  client_type?: string
  is_new_client?: boolean
  precio_mensual?: number | null
  whatsapp_last_at?: string | null
  finance_exclusion?: string | null
}

const EXCLUSION_REASONS = [
  { id: 'prueba', label: 'Prueba' },
  { id: 'demo', label: 'Demo' },
  { id: 'cortesia', label: 'Cortesía' },
  { id: 'propio', label: 'Propio' },
]

const DAY_PRESETS = [7, 15, 30, 60, 90]
const ADJUST_PRESETS = [-30, -15, -7, 7, 15, 30, 60, 90]
const ESTADOS = [
  { id: 'activo', label: 'Activo' },
  { id: 'inactivo', label: 'Inactivo' },
  { id: 'pago_pendiente', label: 'Pago pendiente' },
  { id: 'revisado', label: 'Revisado' },
]

function planDurationDays(plans: any[], planType?: string | null) {
  const p = (plans || []).find((x: any) => x.name === planType)
  const n = Number(p?.durationDays ?? p?.duration_days)
  return Number.isFinite(n) && n > 0 ? n : 30
}

function formatDate(v?: string | null) {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-CO')
}

function formatDateTime(v?: string | null) {
  if (!v) return '—'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })
}

function badge(estado: string) {
  if (estado === 'activo') return 'badge-green'
  if (estado === 'revisado') return 'bg-sky-100 text-sky-800 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
  if (estado === 'pago_pendiente') return 'bg-amber-100 text-amber-800 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'
  return 'badge-red'
}

function waUrl(phone?: string, name?: string) {
  let dig = String(phone || '').replace(/\D/g, '')
  if (!dig) return null
  if (dig.length === 10 && dig.startsWith('3')) dig = `57${dig}`
  const msg = encodeURIComponent(`Hola ${name || ''}, te escribimos desde Food Plan sobre tu plan.`.trim())
  return `https://wa.me/${dig}?text=${msg}`
}

function FoodModal({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: string
  subtitle?: string
  onClose: () => void
  children: ReactNode
}) {
  return createPortal(
    <div
      className="food-plan-root food-portal-modal fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="food-demo-modal-panel w-full max-w-md rounded-2xl bg-white border border-gray-200 shadow-xl p-5"
        style={{ color: '#111827' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-bold text-[#111827]">{title}</h2>
            {subtitle ? <p className="text-sm text-gray-500">{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}

export default function FoodAdminVigenciasPage() {
  const me = useSelector((s: RootState) => s.auth.user)
  const canEditPlan = me?.role === 'SUPER_ADMIN' || me?.role === 'ADMIN'
  const canManagePending = canEditPlan
  const [data, setData] = useState<{ pending: Lic[]; expiring: Lic[]; licenses: Lic[]; alerts: Lic[] }>({
    pending: [], expiring: [], licenses: [], alerts: [],
  })
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<'alerts' | 'pending' | 'expiring' | 'licenses'>(
    me?.role === 'TRAINER' ? 'licenses' : 'pending',
  )
  const [clientFilter, setClientFilter] = useState('')
  const [estadoFilter, setEstadoFilter] = useState('')
  const [search, setSearch] = useState('')
  const [confirmRow, setConfirmRow] = useState<Lic | null>(null)
  const [extendRow, setExtendRow] = useState<Lic | null>(null)
  const [days, setDays] = useState('30')
  const [amount, setAmount] = useState('')
  const [saving, setSaving] = useState(false)
  const [plans, setPlans] = useState<any[]>([])
  const [planType, setPlanType] = useState('BASIC')
  const [markRow, setMarkRow] = useState<Lic | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    api.get('/subscriptions/admin/overview')
      .then((r) => setData({
        pending: r.data?.pending || [],
        expiring: r.data?.expiring || [],
        licenses: r.data?.licenses || [],
        alerts: r.data?.alerts || r.data?.notifications || [],
      }))
      .catch(() => toast.error('No se pudo cargar vigencias'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (canManagePending) return
    if (tab === 'pending') setTab('licenses')
    if (clientFilter === 'pending_payment') setClientFilter('')
    if (estadoFilter === 'pago_pendiente') setEstadoFilter('')
  }, [canManagePending, tab, clientFilter, estadoFilter])
  useEffect(() => {
    api.get('/subscriptions/plans').then((r) => {
      const list = Array.isArray(r.data) ? r.data : []
      setPlans(list)
      if (list[0]?.name) setPlanType(list[0].name)
    }).catch(() => {})
  }, [])

  const matchClient = (row: Lic) => {
    if (clientFilter === 'sin_ingreso') return Boolean(row.finance_exclusion)
    if (row.finance_exclusion) return false
    if (!clientFilter) return true
    if (clientFilter === 'new_client') return row.is_new_client === true || row.client_type === 'nuevo'
    if (clientFilter === 'old_client') return row.is_new_client !== true && row.client_type !== 'nuevo'
    if (clientFilter === 'pending_payment') return row.estado === 'pago_pendiente'
    if (clientFilter === 'revisado') return row.estado === 'revisado'
    return true
  }

  const matchRow = (row: Lic, withEstado = false) => {
    const q = search.trim().toLowerCase()
    if (withEstado && estadoFilter && row.estado !== estadoFilter) return false
    if (!matchClient(row)) return false
    if (!q) return true
    return `${row.usuario_nombre} ${row.usuario_email} ${row.plan}`.toLowerCase().includes(q)
  }

  const filteredLicenses = useMemo(() => {
    return (data.licenses || []).filter((r) => matchRow(r, true))
  }, [data.licenses, clientFilter, estadoFilter, search])

  const filteredAlerts = useMemo(() => {
    return (data.alerts || []).filter((r) => matchRow(r, true))
  }, [data.alerts, clientFilter, estadoFilter, search])

  const filteredPending = useMemo(() => {
    return (data.pending || []).filter((r) => matchRow(r, false))
  }, [data.pending, clientFilter, search])

  const filteredExpiring = useMemo(() => {
    return (data.expiring || []).filter((r) => matchRow(r, true))
  }, [data.expiring, clientFilter, estadoFilter, search])

  const estadoOptions = canManagePending ? ESTADOS : ESTADOS.filter((e) => e.id !== 'pago_pendiente')

  const tabs = [
    { id: 'alerts' as const, label: 'Alertas', count: filteredAlerts.length },
    ...(canManagePending
      ? [{ id: 'pending' as const, label: 'Pendientes', count: filteredPending.length }]
      : []),
    { id: 'expiring' as const, label: 'Por vencer', count: filteredExpiring.length },
    { id: 'licenses' as const, label: 'Licencias', count: filteredLicenses.length },
  ]

  const warnShell = (res: any) => {
    const shell = res?.shell
    if (!shell) return
    const inner = shell.data && typeof shell.data === 'object' ? { ...shell, ...shell.data } : shell
    if (inner.ok === true) return
    if (inner.skipped) {
      toast.error('Se guardó en Food Plan, pero no se encontró la cuenta en el ecosistema. Revisa el vínculo.')
      return
    }
    toast.error('Food Plan se actualizó, pero el ecosistema no alineó la licencia. Revisa o reintenta.')
  }

  const doConfirm = async () => {
    if (!confirmRow) return
    if (!canManagePending && confirmRow.estado === 'pago_pendiente') return
    setSaving(true)
    try {
      const r = await api.post(`/subscriptions/admin/confirm/${confirmRow.user_id}`, {
        days: Number(days),
        amountPaid: amount === '' ? undefined : Number(amount),
        ...(canEditPlan ? { planType } : {}),
      })
      toast.success(r.data?.message || 'Confirmado')
      warnShell(r.data)
      setConfirmRow(null)
      load()
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'No se pudo confirmar')
    } finally { setSaving(false) }
  }

  const doExtend = async () => {
    if (!extendRow) return
    setSaving(true)
    try {
      const r = await api.put(`/subscriptions/user/${extendRow.user_id}/extend`, { days: Number(days) })
      toast.success(r.data?.message || 'Vigencia ampliada')
      warnShell(r.data)
      setExtendRow(null)
      load()
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'No se pudo ampliar')
    } finally { setSaving(false) }
  }

  const setEstado = async (row: Lic, estado: string) => {
    if (estado === row.estado) return
    if (!canManagePending && (estado === 'pago_pendiente' || row.estado === 'pago_pendiente')) return
    if (estado === 'activo' && row.estado !== 'revisado') {
      setDays('30')
      setAmount('')
      if (row.plan_type) setPlanType(row.plan_type)
      setConfirmRow(row)
      return
    }
    try {
      const r = await api.put(`/subscriptions/admin/user/${row.user_id}/status`, { estado })
      toast.success(r.data?.message || 'Estado actualizado')
      warnShell(r.data)
      load()
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'No se pudo cambiar el estado')
    }
  }

  const setExclusion = async (row: Lic, reason: string | null) => {
    setSaving(true)
    try {
      await api.put(`/subscriptions/admin/user/${row.user_id}/finance-exclusion`, { reason })
      toast.success(reason ? 'Marcado sin ingreso' : 'Vuelve a contar en los tableros')
      setMarkRow(null)
      load()
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'No se pudo guardar la marca')
    } finally { setSaving(false) }
  }

  const setPlan = async (row: Lic, nextPlan: string) => {
    if (!nextPlan) return
    const dur = planDurationDays(plans, nextPlan)
    if (!window.confirm(`El plan se ajustará a ${dur} días desde hoy. Solo afecta Food Plan, no D28D ni Training.`)) return
    try {
      const r = await api.put(`/subscriptions/admin/user/${row.user_id}/plan`, { planType: nextPlan })
      toast.success(r.data?.message || 'Plan actualizado')
      warnShell(r.data)
      load()
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'No se pudo cambiar el plan')
    }
  }

  const exportCsv = (rows: Lic[]) => {
    if (!rows.length) {
      toast.error('No hay filas para exportar')
      return
    }
    const header = ['Nombre', 'Email', 'Teléfono', 'Plan', 'Estado', 'Inicio', 'Vence', 'Días', 'WhatsApp']
    const lines = [header.join(',')]
    rows.forEach((r) => {
      lines.push([
        `"${(r.usuario_nombre || '').replace(/"/g, '""')}"`,
        r.usuario_email,
        r.usuario_telefono || '',
        `"${(r.plan || '').replace(/"/g, '""')}"`,
        r.estado,
        formatDate(r.start_date),
        formatDate(r.valid_until),
        r.days_left ?? '',
        formatDateTime(r.whatsapp_last_at),
      ].join(','))
    })
    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `vigencias_food_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
  }

  const openWa = async (row: Lic) => {
    const url = waUrl(row.usuario_telefono, row.usuario_nombre)
    if (!url) {
      toast.error('Sin teléfono')
      return
    }
    try {
      await api.post(`/subscriptions/admin/user/${row.user_id}/whatsapp-contact`)
      load()
    } catch {
      toast.error('Se abrió WhatsApp, pero no se guardó la fecha. Reintenta.')
    }
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const renderTable = (rows: Lic[], opts: { confirm?: boolean; extend?: boolean; estado?: boolean }) => (
    <div className="overflow-x-auto bg-white rounded-2xl border border-gray-100">
      <table className="w-full text-sm">
        <thead className="bg-[#f9fafb]">
          <tr>
            {['Nombre', 'Plan', 'Estado', 'Vence', 'Días', 'WhatsApp', 'Acciones'].map((h) => (
              <th key={h} className="text-left text-xs font-semibold text-gray-500 px-3 py-2">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {rows.length === 0 ? (
            <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-500">Sin registros.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.user_id} className="hover:bg-[#64ba30]/5">
              <td className="px-3 py-2">
                <p className="font-medium text-[#111827]">
                  {r.usuario_nombre}
                  {r.finance_exclusion ? (
                    <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-stone-200 text-stone-700">
                      Sin ingreso · {EXCLUSION_REASONS.find((x) => x.id === r.finance_exclusion)?.label || r.finance_exclusion}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-gray-500">{r.usuario_email}</p>
              </td>
              <td className="px-3 py-2">
                {canEditPlan ? (
                  <select
                    className="input-field text-xs py-1 min-w-[120px]"
                    value={r.plan_type || ''}
                    onChange={(e) => setPlan(r, e.target.value)}
                  >
                    {!r.plan_type && <option value="">Sin plan</option>}
                    {r.plan_type && !(plans || []).some((p: any) => p.name === r.plan_type) && (
                      <option value={r.plan_type}>{r.plan || r.plan_type}</option>
                    )}
                    {(plans.length ? plans : [{ name: 'BASIC', displayName: 'Basic' }]).map((p: any) => (
                      <option key={p.name} value={p.name}>{p.displayName || p.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-xs text-[#111827]">{r.plan || r.plan_type || 'Sin plan'}</span>
                )}
              </td>
              <td className="px-3 py-2">
                {opts.estado && (canManagePending || r.estado !== 'pago_pendiente') ? (
                  <select
                    className="input-field text-xs py-1"
                    value={r.estado}
                    onChange={(e) => setEstado(r, e.target.value)}
                  >
                    {estadoOptions.map((e) => (
                      <option key={e.id} value={e.id}>{e.label}</option>
                    ))}
                  </select>
                ) : (
                  <span className={badge(r.estado)}>{r.estado.replace('_', ' ')}</span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-600">{formatDate(r.valid_until)}</td>
              <td className="px-3 py-2">
                <span className={`text-xs font-medium ${(r.days_left ?? 99) <= 5 ? 'text-red-500' : (r.days_left ?? 99) <= 15 ? 'text-amber-600' : 'text-green-600'}`}>
                  {r.days_left == null ? '—' : `${r.days_left}d`}
                </span>
              </td>
              <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{formatDateTime(r.whatsapp_last_at)}</td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap gap-1">
                  {opts.confirm && (
                    canManagePending
                      ? (r.estado === 'pago_pendiente' || r.estado === 'inactivo')
                      : r.estado === 'inactivo'
                  ) && (
                    <button type="button" className="btn-primary text-xs py-1 px-2" onClick={() => { setDays('30'); setAmount(''); if (r.plan_type) setPlanType(r.plan_type); setConfirmRow(r) }}>
                      Confirmar
                    </button>
                  )}
                  {opts.extend && (r.estado === 'activo' || r.estado === 'revisado') && (
                    <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={() => { setDays('30'); setExtendRow(r) }}>
                      Ajustar días
                    </button>
                  )}
                  {canEditPlan && r.plan_type && r.days_left != null && Math.abs(Number(r.days_left) - planDurationDays(plans, r.plan_type)) > 1 && (
                    <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={() => setPlan(r, r.plan_type || 'BASIC')}>
                      Ajustar al plan
                    </button>
                  )}
                  {canEditPlan ? (
                    r.finance_exclusion ? (
                      <button type="button" className="btn-secondary text-xs py-1 px-2" onClick={() => setExclusion(r, null)} disabled={saving}>
                        Volver a contar
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="relative inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-amber-800 hover:bg-amber-50"
                        title="Sin ingreso"
                        aria-label="Sin ingreso"
                        onClick={() => setMarkRow(r)}
                      >
                        <CircleDollarSign size={16} aria-hidden />
                        <span className="absolute w-4 h-px bg-current -rotate-45" aria-hidden />
                      </button>
                    )
                  ) : null}
                  <button type="button" className="p-1.5 text-gray-400 hover:text-[#64ba30]" title="WhatsApp" onClick={() => openWa(r)}>
                    <MessageCircle size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="p-6 max-w-6xl mx-auto" style={{ color: '#111827' }}>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#64ba30]/10 flex items-center justify-center">
            <CalendarRange size={20} className="text-[#64ba30]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#111827]">Vigencias Food Plan</h1>
            <p className="text-sm text-gray-500">
              {me?.role === 'TRAINER'
                ? 'Solo tus asesorados. Puedes ampliar y marcar estado; el plan y los pendientes de pago los gestiona el admin.'
                : 'Confirma altas, revisa vencimientos y amplía planes. No mezcla D28D ni Training.'}
            </p>
          </div>
        </div>
        <button type="button" className="btn-secondary flex items-center gap-2 text-sm" onClick={() => exportCsv(tab === 'alerts' ? filteredAlerts : tab === 'pending' ? filteredPending : tab === 'expiring' ? filteredExpiring : filteredLicenses)}>
          <Download size={14} /> CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { id: '', label: 'Todos' },
          { id: 'new_client', label: 'Nuevo' },
          { id: 'old_client', label: 'Antiguo' },
          ...(canManagePending ? [{ id: 'pending_payment', label: 'Pendiente por pago' }] : []),
          { id: 'revisado', label: 'Revisado' },
          { id: 'sin_ingreso', label: 'Sin ingreso' },
        ].map((f) => (
          <button
            key={f.id || 'all'}
            type="button"
            onClick={() => setClientFilter(f.id)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border ${clientFilter === f.id ? 'bg-[#64ba30] text-white border-[#64ba30]' : 'bg-white text-gray-600 border-gray-200'}`}
          >
            {f.label}
          </button>
        ))}
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" />
          <input
            className="input input-with-leading-icon w-full text-sm"
            placeholder="Buscar nombre o correo…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {(tab === 'licenses' || tab === 'expiring' || tab === 'alerts') && (
          <select className="input-field text-sm max-w-[180px]" value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)}>
            <option value="">Estado</option>
            {estadoOptions.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
          </select>
        )}
      </div>

      <div className="flex gap-1 mb-4 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`text-sm font-semibold px-3 py-2 rounded-xl whitespace-nowrap ${tab === t.id ? 'bg-[#64ba30] text-white' : 'bg-white text-gray-600 border border-gray-100'}`}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 py-12 text-center">Cargando vigencias…</p>
      ) : tab === 'alerts' ? (
        renderTable(filteredAlerts, { confirm: true, extend: false, estado: true })
      ) : tab === 'pending' && canManagePending ? (
        renderTable(filteredPending, { confirm: true, extend: false, estado: true })
      ) : tab === 'expiring' ? (
        renderTable(filteredExpiring, { confirm: false, extend: true, estado: true })
      ) : (
        renderTable(filteredLicenses, { confirm: true, extend: true, estado: true })
      )}

      {confirmRow && (
        <FoodModal title="Confirmar pago / activar" subtitle={`${confirmRow.usuario_nombre} · ${confirmRow.plan}`} onClose={() => !saving && setConfirmRow(null)}>
          {canEditPlan ? (
            <>
              <label className="text-xs font-medium text-gray-600">Plan</label>
              <select className="input-field mb-3" value={planType} onChange={(e) => setPlanType(e.target.value)}>
                {(plans.length ? plans : [{ name: 'BASIC', displayName: 'Basic' }]).map((p: any) => (
                  <option key={p.name} value={p.name}>{p.displayName || p.name}</option>
                ))}
              </select>
            </>
          ) : (
            <p className="text-sm text-gray-600 mb-3">Plan: <span className="font-medium text-[#111827]">{confirmRow.plan}</span></p>
          )}
          <p className="text-xs font-medium text-gray-600 mb-1">Días de vigencia</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {DAY_PRESETS.map((d) => (
              <button key={d} type="button" onClick={() => setDays(String(d))} className={`text-xs px-2 py-1 rounded-lg border ${days === String(d) ? 'bg-[#64ba30] text-white border-[#64ba30]' : 'border-gray-200'}`}>
                {d}
              </button>
            ))}
          </div>
          <input className="input-field mb-3" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
          <label className="text-xs font-medium text-gray-600">Monto cobrado (opcional)</label>
          <input className="input-field mb-4" type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
          <button type="button" className="btn-primary w-full" disabled={saving} onClick={doConfirm}>
            {saving ? 'Guardando…' : 'Activar Food Plan'}
          </button>
        </FoodModal>
      )}

      {extendRow && (
        <FoodModal title="Ajustar vigencia Food" subtitle={`${extendRow.usuario_nombre} · ${extendRow.plan}`} onClose={() => !saving && setExtendRow(null)}>
          <p className="text-xs text-gray-500 mb-2">Suma o resta días solo de Food Plan. No modifica D28D ni Training.</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {(canEditPlan ? ADJUST_PRESETS : DAY_PRESETS).map((d) => (
              <button key={d} type="button" onClick={() => setDays(String(d))} className={`text-xs px-2 py-1 rounded-lg border ${days === String(d) ? 'bg-[#64ba30] text-white border-[#64ba30]' : 'border-gray-200'}`}>
                {d > 0 ? `+${d}` : d}
              </button>
            ))}
          </div>
          <input className="input-field mb-4" type="number" min={canEditPlan ? -365 : 1} max={365} value={days} onChange={(e) => setDays(e.target.value)} />
          <button type="button" className="btn-primary w-full" disabled={saving || Number(days) === 0} onClick={doExtend}>
            {saving ? 'Guardando…' : `${Number(days) < 0 ? 'Restar' : 'Ampliar'} ${Math.abs(Number(days) || 0)} días`}
          </button>
        </FoodModal>
      )}

      {markRow && canEditPlan && (
        <FoodModal title="Sin ingreso" subtitle={markRow.usuario_nombre} onClose={() => !saving && setMarkRow(null)}>
          <p className="text-sm text-gray-600 mb-3">Sigue entrando a Food. No suma en Vigencias ni en Finanzas.</p>
          <div className="grid grid-cols-2 gap-2">
            {EXCLUSION_REASONS.map((reason) => (
              <button key={reason.id} type="button" className="btn-secondary" disabled={saving} onClick={() => setExclusion(markRow, reason.id)}>
                {reason.label}
              </button>
            ))}
          </div>
        </FoodModal>
      )}
    </div>
  )
}
